import React, { useState, useEffect } from 'react';
import apiService from '../services/api';
import { Gusto, Ingrediente as IngredienteDB, CategoriaGusto, Allergene } from '../types';
import GelatoDataGrid from '../components/DataGrid/GelatoDataGrid';
import { GridApi } from 'ag-grid-community';
import IngredientiOrdinabili from '../components/IngredientiOrdinabili';
import { Pencil, Copy, Lock, Unlock, Camera } from 'lucide-react';
import './GustiPage.css';
import './GustiPage-pagination.css';

interface Ingrediente {
  ingrediente_id: number;
  nome: string;
  quantita: number;
  unita: string;
  ordine?: number;
  is_variegatura?: boolean;
  is_aggiunta?: boolean;
}

interface GustoFormData {
  nome: string;
  nome_secondario: string;
  descrizione: string;
  categoria: string;
  colore: string;
  ingredienti: Ingrediente[];
  allergeni: string[];
  stagionale: boolean;
  attivo: boolean;
  note_produzione: string;
}

const GustiPage: React.FC = () => {
  const [gusti, setGusti] = useState<Gusto[]>([]);
  const [ingredientiDB, setIngredientiDB] = useState<IngredienteDB[]>([]);
  const [categorieGusti, setCategorieGusti] = useState<CategoriaGusto[]>([]);
  const [allergeni, setAllergeni] = useState<Allergene[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategoria, setFilterCategoria] = useState('');
  const [includiDisattivi, setIncludiDisattivi] = useState(false);
  const [vistaTabella, setVistaTabella] = useState(false); // Nuovo stato per vista tabella
  const [showModal, setShowModal] = useState(false);
  const [editingGusto, setEditingGusto] = useState<Gusto | null>(null);
  // Stato separato per la conferma di attivazione/disattivazione
  const [statusChangeGusto, setStatusChangeGusto] = useState<Gusto | null>(null);
  
  // AG Grid state
  const [, setGridApi] = useState<GridApi | null>(null);
  
  const [formData, setFormData] = useState<GustoFormData>({
    nome: '',
    nome_secondario: '',
    descrizione: '',
    categoria: '',
    colore: '#ffffff',
    ingredienti: [],
    allergeni: [],
    stagionale: false,
    attivo: true,
    note_produzione: ''
  });

  useEffect(() => {
    loadGusti();
    loadIngredienti();
    loadCategorieGusti();
    loadAllergeni();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- ricarica solo al toggle dei disattivi; le funzioni load* sono ricreate ad ogni render
  }, [includiDisattivi]);

  const loadIngredienti = async () => {
    try {
      const response = await apiService.getIngredienti(false); // Solo ingredienti attivi
      setIngredientiDB(response);
    } catch (err) {
      console.error('Errore nel caricamento degli ingredienti:', err);
    }
  };

  const loadCategorieGusti = async () => {
    try {
      const response = await apiService.getCategorieGusti();
      setCategorieGusti(response);
    } catch (err) {
      console.error('Errore nel caricamento delle categorie:', err);
    }
  };

  const loadAllergeni = async () => {
    try {
      const response = await apiService.getAllergeni();
      setAllergeni(response);
    } catch (err) {
      console.error('Errore nel caricamento degli allergeni:', err);
    }
  };

  const loadGusti = async () => {
    try {
      setLoading(true);
      const response = await apiService.getGusti(includiDisattivi);
      setGusti(response);
      setError(null);
    } catch (err) {
      setError('Errore nel caricamento dei gusti');
      console.error('Errore:', err);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Carica la foto di un gusto.
   * Il file viene ritagliato quadrato dal server: la foto serve al monitor in
   * gelateria, dove i gusti stanno dentro dei cerchi.
   */
  const handleFoto = async (gusto: Gusto, file: File | undefined) => {
    if (!file) return;
    try {
      setError(null);
      await apiService.caricaFotoGusto(gusto.id, file);
      await loadGusti();
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Errore nel caricamento della foto');
    }
  };

  const handleRimuoviFoto = async (gusto: Gusto) => {
    try {
      setError(null);
      await apiService.rimuoviFotoGusto(gusto.id);
      await loadGusti();
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Errore nella rimozione della foto');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    try {
      // Assicuriamoci che i nomi degli ingredienti siano corretti prima dell'invio
      // Questo è fondamentale per garantire la coerenza tra duplicazione e modifica
      const ingredientiVerificati = formData.ingredienti.map(ing => {
        const ingredienteDB = ingredientiDB.find(i => i.id === ing.ingrediente_id);
        // Se troviamo l'ingrediente nel DB, usiamo SEMPRE il suo nome ufficiale
        const nomeCorretto = ingredienteDB?.nome || ing.nome;
        return {
          ...ing,
          nome: nomeCorretto
        };
      });
      

      const dataToSend = {
        ...formData,
        ingredienti: JSON.stringify(ingredientiVerificati),
        allergeni: formData.allergeni.join(', ')
      };


      if (editingGusto) {
        await apiService.updateGusto(editingGusto.id, dataToSend);
        // 🔥 Trigger ricalcolo automatico dopo aggiornamento
        console.log('🔄 Triggering automatic recalculation after gusto update...');
      } else {
        await apiService.createGusto(dataToSend);
        // 🔥 Trigger ricalcolo automatico dopo creazione
        console.log('🔄 Triggering automatic recalculation after gusto creation...');
      }
      
      setShowModal(false);
      setEditingGusto(null);
      resetForm();
      // 🔥 Ricarica i gusti per vedere i valori aggiornati
      await loadGusti();
    } catch (err: any) {
      console.error('Error in handleSubmit:', err);
      setError(err.message || 'Errore nel salvare il gusto');
    }
  };

  const handleEdit = (gusto: Gusto) => {
    
    setEditingGusto(gusto);
    
    let ingredientiParsed: Ingrediente[] = [];
    if (gusto.ingredienti) {
      try {
        const parsed = JSON.parse(gusto.ingredienti);
        // Converti dal vecchio formato al nuovo se necessario
        ingredientiParsed = parsed.map((ing: any) => {
          // Mantieni l'unità specificata o usa l'unità predefinita
          // Se l'unità salvata è compatibile con il tipo di ingrediente, la manteniamo
          const unitaSalvata = ing.unita || 'g';
          
          // Cerchiamo l'ingrediente nel DB per ottenere la sua unità di misura base
          const ingredienteDB = ingredientiDB.find(i => i.id === ing.ingrediente_id);
          let unitaFinale = unitaSalvata;
          
          // Verifica che l'unità salvata sia compatibile con il tipo di ingrediente
          if (ingredienteDB) {
            if (ingredienteDB.unita_misura === 'kg' && unitaSalvata !== 'g' && unitaSalvata !== 'kg') {
              unitaFinale = 'g'; // Unità predefinita per solidi
            } else if (ingredienteDB.unita_misura === 'l' && unitaSalvata !== 'ml' && unitaSalvata !== 'l') {
              unitaFinale = 'ml'; // Unità predefinita per liquidi
            }
          }
          
          // Utilizziamo SEMPRE il nome dal DB se disponibile per garantire coerenza
          // tra duplicazione e modifica
          const nomeFinaleDaDB = ingredienteDB?.nome || ing.nome;
          
          return {
            ingrediente_id: ing.ingrediente_id || 0,
            nome: nomeFinaleDaDB, // Usa il nome dal DB se disponibile
            quantita: ing.quantita || 0,
            unita: unitaFinale,
            ordine: ing.ordine || 0,
            is_variegatura: ing.is_variegatura || false,
            is_aggiunta: ing.is_aggiunta || false
          };
        });
        
        // Ordina gli ingredienti per ordine salvato
        ingredientiParsed.sort((a, b) => (a.ordine || 0) - (b.ordine || 0));
      } catch (e) {
        // Se non è JSON, trattiamo come stringa semplice
        ingredientiParsed = gusto.ingredienti.split(',').map(ing => ({
          ingrediente_id: 0,
          nome: ing.trim(),
          quantita: 0,
          unita: 'g',
          is_variegatura: false,
          is_aggiunta: false
        }));
      }
    }
    
    setFormData({
      nome: gusto.nome,
      nome_secondario: gusto.nome_secondario || '',
      descrizione: gusto.descrizione || '',
      categoria: gusto.categoria,
      colore: gusto.colore,
      ingredienti: ingredientiParsed,
      allergeni: gusto.allergeni ? gusto.allergeni.split(', ') : [],
      stagionale: gusto.stagionale || false,
      attivo: gusto.attivo,
      note_produzione: gusto.note_produzione || ''
    });
    setShowModal(true);
  };

  const handleDuplicate = (gusto: Gusto) => {
    
    // Impostiamo editingGusto a null per indicare che stiamo creando un nuovo gusto
    setEditingGusto(null);
    
    let ingredientiParsed: Ingrediente[] = [];
    if (gusto.ingredienti) {
      try {
        const parsed = JSON.parse(gusto.ingredienti);
        
        // Converti dal vecchio formato al nuovo se necessario
        ingredientiParsed = parsed.map((ing: any) => {
          // Mantieni l'unità specificata o usa l'unità predefinita
          const unitaSalvata = ing.unita || 'g';
          
          // Cerchiamo l'ingrediente nel DB per ottenere la sua unità di misura base
          const ingredienteDB = ingredientiDB.find(i => i.id === ing.ingrediente_id);
          let unitaFinale = unitaSalvata;
          
          // Verifica che l'unità salvata sia compatibile con il tipo di ingrediente
          if (ingredienteDB) {
            if (ingredienteDB.unita_misura === 'kg' && unitaSalvata !== 'g' && unitaSalvata !== 'kg') {
              unitaFinale = 'g'; // Unità predefinita per solidi
            } else if (ingredienteDB.unita_misura === 'l' && unitaSalvata !== 'ml' && unitaSalvata !== 'l') {
              unitaFinale = 'ml'; // Unità predefinita per liquidi
            }
          }
          
          const nuovoIngrediente = {
            ingrediente_id: ing.ingrediente_id || 0,
            nome: ingredienteDB?.nome || ing.nome,  // Usa il nome dal DB se disponibile
            quantita: ing.quantita || 0,
            unita: unitaFinale,
            ordine: ing.ordine || 0,
            is_variegatura: ing.is_variegatura || false,
            is_aggiunta: ing.is_aggiunta || false
          };
          
          return nuovoIngrediente;
        });
        
        // Ordina gli ingredienti per ordine salvato
        ingredientiParsed.sort((a, b) => (a.ordine || 0) - (b.ordine || 0));
      } catch (e) {
        console.error('Errore nel parsing degli ingredienti:', e);
        // Se non è JSON, trattiamo come stringa semplice
        ingredientiParsed = gusto.ingredienti.split(',').map((ing, index) => {
          const nuovoIngrediente = {
            ingrediente_id: 0,
            nome: ing.trim(),
            quantita: 0,
            unita: 'g',
            ordine: index + 1,
            is_variegatura: false,
            is_aggiunta: false
          };
          return nuovoIngrediente;
        });
      }
    }
    
    
    // Prepariamo i dati del nuovo gusto duplicato
    const newFormData = {
      nome: `${gusto.nome} - Copia`,
      descrizione: gusto.descrizione || '',
      categoria: gusto.categoria,
      colore: gusto.colore,
      ingredienti: ingredientiParsed,
      allergeni: gusto.allergeni ? gusto.allergeni.split(', ') : [],
      stagionale: gusto.stagionale || false,
      attivo: true, // Il nuovo gusto duplicato sarà sempre attivo
      note_produzione: gusto.note_produzione || '',
      nome_secondario: gusto.nome_secondario || ''
    };
    
    setFormData(newFormData);
    
    // Mostra un messaggio per indicare che si sta duplicando un gusto
    setError(null);
    setTimeout(() => {
      // Usa setTimeout per assicurarsi che il messaggio appaia dopo che il modal è visibile
      const modalTitle = document.querySelector('.modal-title');
      if (modalTitle) {
        modalTitle.innerHTML = `Nuovo gusto <span style="color: var(--gelato-pistacchio);">(duplicato da "${gusto.nome}")</span>`;
      }
    }, 100);
    
    setShowModal(true);
  };

  // Mostra la conferma prima di attivare/disattivare un gusto
  const handleToggleAttivoConfirm = (gusto: Gusto) => {
    setStatusChangeGusto(gusto);
  };
  
  // Esegue effettivamente l'attivazione/disattivazione
  const handleToggleAttivo = async (id: number, attivo: boolean) => {
    try {
      if (attivo) {
        await apiService.attivaGusto(id);
      } else {
        await apiService.disattivaGusto(id);
      }
      await loadGusti();
      // Resetta lo stato di conferma
      setStatusChangeGusto(null);
    } catch (err: any) {
      setError('Errore nell\'aggiornamento dello stato del gusto');
    }
  };

  const resetForm = () => {
    setFormData({
      nome: '',
      nome_secondario: '',
      descrizione: '',
      categoria: '',
      colore: '#ffffff',
      ingredienti: [],
      allergeni: [],
      stagionale: false,
      attivo: true,
      note_produzione: ''
    });
  };

  const addIngrediente = () => {
    if (ingredientiDB.length === 0) {
      setError('Nessun ingrediente disponibile. Aggiungi ingredienti dalla sezione Ingredienti.');
      return;
    }
    
    // Ottieni l'unità di misura predefinita
    const unitaMisura = ingredientiDB[0].unita_misura;
    
    // Imposta l'unità predefinita basandosi sull'unità di misura dell'ingrediente
    // Ora usiamo direttamente l'unità più piccola (g, ml) come predefinita, ma l'utente potrà cambiarla
    const unitaDefault = unitaMisura === 'kg' ? 'g' : unitaMisura === 'l' ? 'ml' : unitaMisura;
    
    setFormData({
      ...formData,
      ingredienti: [...formData.ingredienti, { 
        ingrediente_id: ingredientiDB[0].id, 
        nome: ingredientiDB[0].nome, 
        quantita: 0, 
        unita: unitaDefault,
        ordine: formData.ingredienti.length + 1,
        is_variegatura: false,
        is_aggiunta: false
      }]
    });
  };

  const removeIngrediente = (index: number) => {
    const nuoviIngredienti = formData.ingredienti.filter((_, i) => i !== index);
    // Riordina gli indici dopo la rimozione
    const ingredientiRiordinati = nuoviIngredienti.map((ing, idx) => ({
      ...ing,
      ordine: idx + 1
    }));
    
    setFormData({
      ...formData,
      ingredienti: ingredientiRiordinati
    });
  };

  const handleReorderIngredienti = (nuoviIngredienti: Ingrediente[]) => {
    // Aggiorna gli ordini basandosi sulla nuova posizione nell'array
    const ingredientiConOrdine = nuoviIngredienti.map((ing, index) => ({
      ...ing,
      ordine: index + 1
    }));
    
    setFormData({
      ...formData,
      ingredienti: ingredientiConOrdine
    });
  };

  const updateIngrediente = (index: number, field: string, value: any) => {
    const newIngredienti = [...formData.ingredienti];
    
    if (field === 'ingrediente_id') {
      // Quando cambia l'ingrediente, aggiorna anche nome e unità
      const ingrediente = ingredientiDB.find(ing => ing.id === value);
      if (ingrediente) {
        // Determina l'unità di misura dell'ingrediente
        const unitaMisura = ingrediente.unita_misura;
        
        // Mantieni l'unità di misura attuale se è compatibile con il nuovo ingrediente
        // altrimenti imposta l'unità predefinita per quel tipo di ingrediente
        let nuovaUnita = newIngredienti[index].unita;
        
        if (unitaMisura === 'kg') {
          // Per ingredienti misurati in kg, accetta solo 'g' o 'kg'
          if (nuovaUnita !== 'g' && nuovaUnita !== 'kg') {
            nuovaUnita = 'g'; // Unità predefinita per solidi
          }
        } else if (unitaMisura === 'l') {
          // Per ingredienti misurati in litri, accetta solo 'ml' o 'l'
          if (nuovaUnita !== 'ml' && nuovaUnita !== 'l') {
            nuovaUnita = 'ml'; // Unità predefinita per liquidi
          }
        } else {
          // Per altre unità di misura (pz, ecc.), usa l'unità originale
          nuovaUnita = unitaMisura;
        }
        
        // Quando l'ingrediente cambia, assicuriamoci di usare il nome dal DB
        
        // Quando l'ingrediente cambia, assicuriamoci di usare SEMPRE il nome dal DB
        // per garantire coerenza tra duplicazione e modifica
        
        newIngredienti[index] = {
          ...newIngredienti[index],
          ingrediente_id: value,
          nome: ingrediente.nome, // Usa sempre il nome dal database
          unita: nuovaUnita
        };
      }
    } else {
      newIngredienti[index] = {
        ...newIngredienti[index],
        [field]: value
      };
    }
    
    setFormData({
      ...formData,
      ingredienti: newIngredienti
    });
  };

  const toggleAllergene = (allergene: string) => {
    const newAllergeni = formData.allergeni.includes(allergene)
      ? formData.allergeni.filter(a => a !== allergene)
      : [...formData.allergeni, allergene];
    setFormData({ ...formData, allergeni: newAllergeni });
  };

  // Calcola il food cost in tempo reale
  const calcolaFoodCostTempoReale = () => {
    return formData.ingredienti.reduce((total, ing) => {
      const ingredienteDB = ingredientiDB.find(i => i.id === ing.ingrediente_id);
      if (!ingredienteDB || !ing.quantita) return total;

      let quantitaInKgOL = ing.quantita;
      
      // Conversione unità
      if (ingredienteDB.unita_misura === 'kg' && ing.unita === 'g') {
        quantitaInKgOL = ing.quantita / 1000;
      } else if (ingredienteDB.unita_misura === 'l' && ing.unita === 'ml') {
        quantitaInKgOL = ing.quantita / 1000;
      }

      return total + (quantitaInKgOL * (ingredienteDB.costo_per_unita || 0));
    }, 0);
  };

  // 🔥 Funzioni per calcoli nutrizionali in tempo reale
  const calcolaMetricheTempoReale = () => {
    let totalePac = 0, totalePod = 0, totaleKcal = 0;

    formData.ingredienti.forEach(ing => {
      const ingredienteDB = ingredientiDB.find(i => i.id === ing.ingrediente_id);
      if (!ingredienteDB || !ing.quantita) return;

      let quantitaInKgOL = ing.quantita;
      
      // Conversione unità  
      if (ingredienteDB.unita_misura === 'kg' && ing.unita === 'g') {
        quantitaInKgOL = ing.quantita / 1000;
      } else if (ingredienteDB.unita_misura === 'l' && ing.unita === 'ml') {
        quantitaInKgOL = ing.quantita / 1000;
      }

      // Calcolo calorie assolute
      const calorie = (ingredienteDB.calorie || 0) * quantitaInKgOL * 10; // kcal/kg -> kcal
      
      // PAC e POD basati sui valori specifici degli ingredienti (non sui macronutrienti)
      const pacIngrediente = (ingredienteDB.pac || 0) * quantitaInKgOL; // PAC * quantità
      const podIngrediente = (ingredienteDB.pod || 0) * quantitaInKgOL; // POD * quantità
      
      totalePac += pacIngrediente; // Somma dei PAC pesati
      totalePod += podIngrediente; // Somma dei POD pesati
      totaleKcal += calorie; // kcal totali
    });

    // Valori assoluti per 1kg di prodotto finale
    const pacTotale = totalePac; // Valore assoluto PAC
    const podTotale = totalePod; // Valore assoluto POD
    const kcalPer100g = totaleKcal / 10; // kcal/kg -> kcal/100g

    return {
      pac: pacTotale,       // Valore assoluto PAC
      pod: podTotale,       // Valore assoluto POD
      kcal: kcalPer100g     // kcal per 100g
    };
  };

  // Filtra i gusti in base alla ricerca e alla categoria
  const filteredGusti = gusti.filter(gusto => {
    const matchesSearch = gusto.nome.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (gusto.descrizione && gusto.descrizione.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesCategory = filterCategoria === '' || gusto.categoria === filterCategoria;
    return matchesSearch && matchesCategory;
  });
  
  // AG Grid functions
  const onGridReady = (params: any) => {
    setGridApi(params.api);
  };

  const onCellClicked = async (params: any) => {
    if (params.colDef.field === 'actions') {
      const target = params.event.target;
      const gusto = params.data;
      
      if (target.classList.contains('btn-edit')) {
        handleEdit(gusto);
      } else if (target.classList.contains('btn-duplicate')) {
        handleDuplicate(gusto);
      } else if (target.classList.contains('btn-toggle')) {
        handleToggleAttivoConfirm(gusto);
      }
    }
  };

  // AG Grid column definitions
  const columnDefs = [
    {
      headerName: 'Nome',
      field: 'nome',
      sortable: true,
      filter: true,
      flex: 2,
      cellRenderer: (params: any) => {
        const gusto = params.data;
        return React.createElement('div', {
          style: { display: 'flex', alignItems: 'center', gap: '8px' }
        }, [
          React.createElement('div', {
            key: 'color',
            style: {
              width: '20px',
              height: '20px',
              borderRadius: '50%',
              backgroundColor: gusto.colore || '#ccc',
              border: '1px solid #ddd'
            }
          }),
          React.createElement('div', { key: 'info', style: { display: 'flex', flexDirection: 'column' } }, [
            React.createElement('strong', { key: 'nome' }, gusto.nome),
            gusto.descrizione && React.createElement('small', { 
              key: 'desc', 
              style: { color: '#666', fontSize: '0.85em' } 
            }, gusto.descrizione)
          ])
        ]);
      }
    },
    {
      headerName: 'Categoria',
      field: 'categoria',
      sortable: true,
      filter: true,
      flex: 1
    },
    {
      headerName: 'Food Cost',
      field: 'food_cost',
      sortable: true,
      filter: true,
      flex: 1,
      cellRenderer: (params: any) => {
        const value = params.value;
        return value !== undefined ? 
          React.createElement('strong', {}, `€${parseFloat(value).toFixed(2)}/kg`) :
          React.createElement('span', { style: { color: '#999' } }, '-');
      }
    },
    {
      headerName: 'PAC',
      field: 'pac',
      sortable: true,
      filter: true,
      flex: 0.8,
      cellRenderer: (params: any) => {
        const value = params.value;
        return value !== undefined ? `${parseFloat(value).toFixed(1)}` : '-';
      }
    },
    {
      headerName: 'POD',
      field: 'pod',
      sortable: true,
      filter: true,
      flex: 0.8,
      cellRenderer: (params: any) => {
        const value = params.value;
        return value !== undefined ? `${parseFloat(value).toFixed(1)}` : '-';
      }
    },
    {
      headerName: 'Kcal',
      field: 'kcal',
      sortable: true,
      filter: true,
      flex: 0.8,
      cellRenderer: (params: any) => {
        const value = params.value;
        return value !== undefined ? `${parseFloat(value).toFixed(0)}/100g` : '-';
      }
    },
    {
      headerName: 'Stato',
      field: 'attivo',
      sortable: true,
      filter: true,
      flex: 1,
      cellRenderer: (params: any) => {
        const gusto = params.data;
        const isActive = params.value;
        return React.createElement('div', {
          style: { display: 'flex', flexDirection: 'column', gap: '4px' }
        }, [
          React.createElement('span', {
            key: 'status',
            className: `badge ${isActive ? 'badge-success' : 'badge-danger'}`
          }, isActive ? 'Attivo' : 'Inattivo'),
          gusto.stagionale && React.createElement('span', {
            key: 'seasonal',
            className: 'badge badge-info',
            style: { fontSize: '0.75em' }
          }, 'Stagionale')
        ]);
      }
    },
    {
      headerName: 'Azioni',
      field: 'actions',
      width: 150,
      sortable: false,
      filter: false,
      pinned: 'right' as const,
      cellRenderer: (params: any) => {
        const buttons = [
          React.createElement('button', {
            className: 'btn-edit',
            title: 'Modifica gusto',
            key: 'edit'
          }, React.createElement(Pencil, { size: 16, style: { pointerEvents: 'none' } })),
          React.createElement('button', {
            className: 'btn-duplicate',
            title: 'Duplica gusto',
            key: 'duplicate'
          }, React.createElement(Copy, { size: 16, style: { pointerEvents: 'none' } })),
          React.createElement('button', {
            className: `btn-toggle ${params.data.attivo ? 'btn-warning' : 'btn-success'}`,
            title: params.data.attivo ? 'Disattiva gusto' : 'Attiva gusto',
            key: 'toggle'
          }, React.createElement(params.data.attivo ? Lock : Unlock, { size: 16, style: { pointerEvents: 'none' } }))
        ];

        return React.createElement('div', {
          className: 'grid-actions-cell',
          'data-row-id': params.data.id
        }, buttons);
      }
    }
  ];

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Caricamento gusti...</p>
      </div>
    );
  }

  return (
    <div className="gusti-page">
      <div className="page-header">
        <h1>Gestione Gusti</h1>
        <button
          onClick={() => setShowModal(true)}
          className="btn btn-primary"
        >
          Nuovo Gusto
        </button>
      </div>

      {error && (
        <div className="error-message">
          {error}
          <button onClick={() => setError(null)}>×</button>
        </div>
      )}

      <div className="filters">
        <div className="search-bar">
          <input
            type="text"
            placeholder="Cerca gusti..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>
        
        <div className="filter-group">
          <select
            value={filterCategoria}
            onChange={(e) => setFilterCategoria(e.target.value)}
            className="filter-select"
          >
            <option value="">Tutte le categorie</option>
            {categorieGusti.filter(cat => cat.attivo).map(cat => (
              <option key={cat.id} value={cat.nome}>{cat.nome}</option>
            ))}
          </select>
        </div>
        
        <div className="checkbox-group">
          <label className="checkbox-label">
            <input
              type="checkbox"
              checked={includiDisattivi}
              onChange={(e) => setIncludiDisattivi(e.target.checked)}
            />
            Includi gusti disattivati
          </label>
        </div>
        
        <div className="view-toggle">
          <span className="toggle-label">Vista:</span>
          <button
            className={`btn-toggle ${!vistaTabella ? 'active' : ''}`}
            onClick={() => setVistaTabella(false)}
          >
            Schede
          </button>
          <button
            className={`btn-toggle ${vistaTabella ? 'active' : ''}`}
            onClick={() => setVistaTabella(true)}
          >
            Tabella
          </button>
        </div>
      </div>

      <div className="gusti-container">
        {vistaTabella ? (
          // Vista Tabella con AG Grid
          <div className="ag-grid-container">
            <GelatoDataGrid
              rowData={filteredGusti}
              columnDefs={columnDefs}
              onGridReady={onGridReady}
              onCellClicked={onCellClicked}
              pagination={true}
              paginationPageSize={20}
              height="600px"
            />
          </div>
        ) : (
          // Vista Schede (default)
          <div className="card-grid">
            {filteredGusti.map(gusto => (
              <div key={gusto.id} className="item-card">
                <div className="item-card-header">
                  <div className="gusto-color-indicator" style={{ backgroundColor: gusto.colore, width: '24px', height: '24px', borderRadius: '50%', marginRight: '8px' }}></div>
                  <h3>{gusto.nome}</h3>
                  <span className={`status-badge ${gusto.attivo ? 'active' : 'inactive'}`}>
                    {gusto.attivo ? 'Attivo' : 'Disattivo'}
                  </span>
                </div>
                
                <div className="item-card-body">
                  <div className="gusto-foto">
                    {gusto.foto_url ? (
                      <img src={gusto.foto_url} alt={gusto.nome} />
                    ) : (
                      <div className="gusto-foto-vuota" style={{ backgroundColor: gusto.colore }}>
                        <Camera size={20} />
                      </div>
                    )}
                  </div>

                  <span className="category-badge">{gusto.categoria}</span>
                  {gusto.descrizione && <p className="description">{gusto.descrizione}</p>}
                  
                  {gusto.note_produzione && (
                    <div className="production-notes">
                      <h4 className="notes-heading">Note di Produzione:</h4>
                      <p className="notes-content">{gusto.note_produzione}</p>
                    </div>
                  )}
                  
                  {gusto.food_cost !== undefined && (
                    <div className="price-info">
                      <span className="price-label">Food Cost:</span>
                      <span className="price-value">€{gusto.food_cost.toFixed(2)}/kg</span>
                    </div>
                  )}
                  
                  {/* Metriche nutrizionali */}
                  <div className="gusto-metrics-enhanced">
                    <div className="metric-box">
                      <span className="metric-label">PAC</span>
                      <span className="metric-value">
                        {gusto.pac !== undefined ? `${gusto.pac.toFixed(1)}` : '-'}
                      </span>
                    </div>
                    <div className="metric-box">
                      <span className="metric-label">POD</span>
                      <span className="metric-value">
                        {gusto.pod !== undefined ? `${gusto.pod.toFixed(1)}` : '-'}
                      </span>
                    </div>
                    <div className="metric-box">
                      <span className="metric-label">Kcal</span>
                      <span className="metric-value">
                        {gusto.kcal !== undefined ? `${gusto.kcal.toFixed(0)}/100g` : '-'}
                      </span>
                    </div>
                  </div>
                  
                  <div className="badge-container">
                    {gusto.stagionale && <span className="feature-badge seasonal">Stagionale</span>}
                  </div>
                  
                  <div className="card-actions">
                    <button
                      onClick={() => handleEdit(gusto)}
                      className="btn btn-secondary btn-sm"
                    >
                      Modifica
                    </button>
                    <button
                      onClick={() => handleDuplicate(gusto)}
                      className="btn btn-info btn-sm"
                    >
                      Duplica
                    </button>
                    <button
                      onClick={() => handleToggleAttivoConfirm(gusto)}
                      className={`btn btn-sm ${gusto.attivo ? 'btn-warning' : 'btn-success'}`}
                    >
                      {gusto.attivo ? 'Disattiva' : 'Attiva'}
                    </button>
                    <label className="btn btn-secondary btn-sm" style={{ cursor: 'pointer' }}>
                      {gusto.foto_url ? 'Cambia foto' : 'Foto'}
                      <input type="file" accept="image/jpeg,image/png,image/webp"
                        style={{ display: 'none' }}
                        onChange={(e) => {
                          handleFoto(gusto, e.target.files?.[0]);
                          // senza questo, riselezionare lo stesso file non
                          // scatena l'evento e sembra che non funzioni
                          e.target.value = '';
                        }} />
                    </label>
                    {gusto.foto_url && (
                      <button
                        onClick={() => handleRimuoviFoto(gusto)}
                        className="btn btn-secondary btn-sm"
                      >
                        Togli foto
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {showModal && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h2>{editingGusto ? 'Modifica Gusto' : 'Nuovo Gusto'}</h2>
              <button onClick={() => setShowModal(false)} className="close-btn">×</button>
            </div>
            
            <form onSubmit={handleSubmit} className="modal-form">
              <div className="form-group">
                <label>Nome *</label>
                <input
                  type="text"
                  required
                  value={formData.nome}
                  onChange={(e) => setFormData({...formData, nome: e.target.value})}
                  placeholder="Nome del gusto"
                />
              </div>

              <div className="form-group">
                <label>Nome nella seconda lingua</label>
                <input
                  type="text"
                  value={formData.nome_secondario}
                  onChange={(e) => setFormData({...formData, nome_secondario: e.target.value})}
                  placeholder="Es. Pistachio, se il primo nome è in italiano"
                />
                <small>
                  Facoltativo. Sul monitor in gelateria i due nomi si alternano sotto la foto,
                  se lo schermo è impostato per farlo.
                </small>
              </div>

              <div className="form-group">
                <label>Categoria *</label>
                <select
                  required
                  value={formData.categoria}
                  onChange={(e) => setFormData({...formData, categoria: e.target.value})}
                >
                  <option value="">Seleziona categoria</option>
                  {categorieGusti.length > 0 ? (
                    categorieGusti.filter(cat => cat.attivo).map(cat => (
                      <option key={cat.id} value={cat.nome}>{cat.nome}</option>
                    ))
                  ) : (
                    <option disabled>Caricamento categorie...</option>
                  )}
                </select>
              </div>

              <div className="form-group">
                <label>Descrizione</label>
                <textarea
                  rows={3}
                  value={formData.descrizione}
                  onChange={(e) => setFormData({...formData, descrizione: e.target.value})}
                  placeholder="Descrizione del gusto"
                />
              </div>

              <div className="form-group">
                <label>Note di Produzione</label>
                <textarea
                  rows={4}
                  value={formData.note_produzione}
                  onChange={(e) => setFormData({...formData, note_produzione: e.target.value})}
                  placeholder="Note e suggerimenti per la produzione di questo gusto (es. temperature, tempi di lavorazione, accorgimenti speciali...)"
                />
              </div>

              <div className="form-group">
                <label>Colore</label>
                <input
                  type="color"
                  value={formData.colore}
                  onChange={(e) => setFormData({...formData, colore: e.target.value})}
                />
              </div>

              <div className="form-group">
                <label>Ingredienti</label>
                <IngredientiOrdinabili
                  ingredienti={formData.ingredienti}
                  onReorder={handleReorderIngredienti}
                  onUpdateIngrediente={updateIngrediente}
                  onRemoveIngrediente={removeIngrediente}
                  ingredientiDB={ingredientiDB}
                  className="ingredienti-form"
                />
                
                <button
                  type="button"
                  onClick={addIngrediente}
                  className="btn btn-secondary btn-sm"
                  style={{ marginTop: '10px' }}
                >
                  Aggiungi Ingrediente
                </button>
              </div>
                
                {formData.ingredienti.length > 0 && (
                  <div className="food-cost-preview">
                    <div className="metrics-summary">
                      <div className="metric-item">
                        <strong>Food Cost: €{calcolaFoodCostTempoReale().toFixed(2)}</strong>
                        <small>Costo per kg</small>
                      </div>

                      <div className="metric-item">
                        <strong>PAC: {calcolaMetricheTempoReale().pac.toFixed(1)}</strong>
                        <small>Potere Anti-Congelante</small>
                      </div>

                      <div className="metric-item">
                        <strong>POD: {calcolaMetricheTempoReale().pod.toFixed(1)}</strong>
                        <small>Potere Dolcificante</small>
                      </div>

                      <div className="metric-item">
                        <strong>Kcal: {calcolaMetricheTempoReale().kcal.toFixed(0)}</strong>
                        <small>Calorie per 100g</small>
                      </div>
                    </div>
                    <small className="metrics-note">
                      PAC e POD valori assoluti, Kcal per 100g di prodotto
                    </small>
                  </div>
                )}

              <div className="form-group">
                <label>Allergeni</label>
                <div className="allergeni-checkboxes">
                  {allergeni.length > 0 ? (
                    allergeni.filter(allergene => allergene.attivo).map(allergene => (
                      <label key={allergene.id} className="checkbox-label">
                        <input
                          type="checkbox"
                          checked={formData.allergeni.includes(allergene.nome)}
                          onChange={() => toggleAllergene(allergene.nome)}
                        />
                        {allergene.nome}
                      </label>
                    ))
                  ) : (
                    <div className="loading-text">Caricamento allergeni...</div>
                  )}
                </div>
              </div>

              <div className="form-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={formData.stagionale}
                    onChange={(e) => setFormData({...formData, stagionale: e.target.checked})}
                  />
                  Gusto stagionale
                </label>
              </div>

              <div className="form-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={formData.attivo}
                    onChange={(e) => setFormData({...formData, attivo: e.target.checked})}
                  />
                  Gusto attivo
                </label>
              </div>

              <div className="form-actions">
                <button type="button" onClick={() => setShowModal(false)} className="btn btn-secondary">
                  Annulla
                </button>
                <button type="submit" className="btn btn-primary">
                  {editingGusto ? 'Aggiorna' : 'Crea'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modale per conferma disattivazione gusto */}
      {statusChangeGusto && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h2>Conferma Azione</h2>
              <button onClick={() => setStatusChangeGusto(null)} className="close-btn">×</button>
            </div>
            
            <div className="modal-content">
              <p>Sei sicuro di voler {statusChangeGusto.attivo ? 'disattivare' : 'attivare'} il gusto "<strong>{statusChangeGusto.nome}</strong>"?</p>
            </div>
            
            <div className="modal-actions">
              <button
                onClick={() => {
                  handleToggleAttivo(statusChangeGusto.id, !statusChangeGusto.attivo);
                }}
                className={`btn ${statusChangeGusto.attivo ? 'btn-warning' : 'btn-success'}`}
              >
                {statusChangeGusto.attivo ? 'Disattiva' : 'Attiva'} Gusto
              </button>
              <button onClick={() => setStatusChangeGusto(null)} className="btn btn-secondary">
                Annulla
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GustiPage;
